import type {
  AdditionalProductsMetadataV2,
  AdditionalProductsOptionsV2,
  GapProductV2,
  VehicleSectionV2,
  VscProductV2,
} from "@credit-acceptance/step6-bff-client";
import { useTranslation } from "react-i18next";

import { CHECKLIST_INCOMPLETE } from "../../checklist/submitChecklist";
import Section from "../../components/Section";
import { ContractVehicleInformation } from "./fields/ContractVehicleInformation";
import { GapInformation } from "./fields/GapInformation";
import { InsuranceInformation } from "./fields/InsuranceInformation";
import { isGapSectionVisible, isVscSectionVisible, resolveGapAccess, resolveVscAccess } from "./fields/productAccess";
import { VehicleServiceContractInformation } from "./fields/VehicleServiceContractInformation";

interface VehicleProperties {
  /** Keep draft-owning forms mounted, but remove their controls, portals, and save registrations. */
  readonly visible?: boolean;
  readonly programType?: string;
  readonly canCallInsurance?: boolean;
  /** Submit checklist verdict for Vehicle (STEP6VEH): "N" flags the section warning icon. */
  readonly checklistStatus?: string;
  /** Vehicle details + insurance from the dashboard response. */
  readonly vehicle?: VehicleSectionV2;
  /** From `financial.tila.amountFinanced` on the dashboard response — lives outside `VehicleSection` itself. */
  readonly amountFinanced?: number;
  /** From `additionalProducts.vsc`. */
  readonly vsc?: VscProductV2;
  /** From `additionalProducts.gap`. */
  readonly gap?: GapProductV2;
  /** From `additionalProducts.contractVsc` — the dealer's RISC values for the VSC popup. */
  readonly contractVsc?: VscProductV2;
  /** From `additionalProducts.contractGap` — the dealer's RISC values for the GAP popup. */
  readonly contractGap?: GapProductV2;
  /** From `additionalProducts.metadata` — TPAP layout, enable flags, and the unreliable-flags warning. */
  readonly productsMetadata?: AdditionalProductsMetadataV2;
  /** From `additionalProducts.options` — the dealer's TPAP vendors per product; a missing list on a TPAP product means unknown. */
  readonly productsOptions?: AdditionalProductsOptionsV2;
  /**
   * Whether the Vehicle tab allows VSC/GAP edits at all (the inverse of spec 4.3's read-only), before the
   * per-field flag rules in `productAccess.ts` apply. Defaults to `editable`; the caller resolves the lock / role gates.
   */
  readonly productsEditable?: boolean;
  /**
   * Whether the Vehicle tab is editable at all. `ReviewPage` passes `!readOnly && permissions.sections.vehicle.editable`
   * (the VLA lock and role/permission verdicts); it defaults to `false`, so the tab fails safe to read-only.
   * Insurance follows it directly; VSC/GAP additionally need `productsEditable` (not pre-approval), and Contract
   * Vehicle's fields follow `contractVehicleEditable` / `cpoDateEditable`, which default to it. Edits persist through
   * the shared save (`vehicle`, `vehicleInsurance`, `additionalProducts`).
   */
  readonly editable?: boolean;
  /** From `financial.vehicle.condition`; NEW makes Contract Vehicle's Total Selling Price editable. */
  readonly condition?: string;
  /** From `financial.contractDate`; Contract Vehicle warns when the CPO date lands well after it. */
  readonly contractDate?: string;
  /** Suppresses Contract Vehicle's CPO contract-date warning, which spec §5.3 exempts in pre-approval. */
  readonly isPreApprovalDeal?: boolean;
  /**
   * Whether Contract Vehicle's Odometer Disc Mileage and CPO Date are typeable (Total Selling Price also
   * needs a NEW vehicle). Defaults to `editable`, so it fails safe to read-only until the caller resolves
   * the VLA-lock / role / deal-state / pre-approval gates.
   */
  readonly contractVehicleEditable?: boolean;
  /**
   * Whether Contract Vehicle's CPO Date is typeable. Defaults to `contractVehicleEditable`. Separate
   * because caps2 also locks it once the deal reaches Contract Completed (its rule is
   * `showSaveButton and applicationStatus != 'Approval'`), which the role-based gate doesn't cover.
   */
  readonly cpoDateEditable?: boolean;
}

// ---------------------------------------------------------------------------
// Vehicle card — collapsible outer card (via Section/Accordion), same as
// Signers, with two rows of two-column subsections: Contract Vehicle |
// Insurance, then Vehicle Service Contract | GAP.
// ---------------------------------------------------------------------------
function Vehicle({
  visible = true,
  vehicle,
  amountFinanced,
  vsc,
  gap,
  contractVsc,
  contractGap,
  productsMetadata,
  productsOptions,
  editable = false,
  condition,
  contractDate,
  isPreApprovalDeal = false,
  contractVehicleEditable = editable,
  cpoDateEditable = contractVehicleEditable,
  productsEditable = editable,
  programType,
  canCallInsurance = false,
  checklistStatus,
}: VehicleProperties) {
  const { t } = useTranslation();
  const vscAccess = resolveVscAccess(productsMetadata, !productsEditable);
  const gapAccess = resolveGapAccess(productsMetadata, !productsEditable);
  const showVsc = isVscSectionVisible(productsMetadata);
  const showGap = isGapSectionVisible(productsMetadata);
  const vendorOptionsUnavailable =
    (showVsc && vscAccess.vendorEnabled && vscAccess.vendorDropdown && !productsOptions?.vsc?.vendors?.length) ||
    (showGap && gapAccess.vendorEnabled && gapAccess.vendorDropdown && !productsOptions?.gap?.vendors?.length);

  return (
    <Section
      title={t("vehicle.sectionTitle")}
      scrollSpyCategory="Vehicle"
      hasError={checklistStatus === CHECKLIST_INCOMPLETE}
      hidden={!visible}
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-x-6 gap-y-3 xl:grid-cols-2">
          <ContractVehicleInformation
            visible={visible}
            vehicle={vehicle}
            condition={condition}
            contractDate={contractDate}
            isPreApprovalDeal={isPreApprovalDeal}
            contractVehicleEditable={contractVehicleEditable}
            cpoDateEditable={cpoDateEditable}
          />
          <InsuranceInformation
            visible={visible}
            insurance={vehicle?.insurance}
            validations={vehicle?.validations}
            amountFinanced={amountFinanced}
            editable={editable}
            programType={programType}
            canCall={canCallInsurance}
          />
        </div>
        {(showVsc || showGap) && (
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 xl:grid-cols-2">
            {showVsc && (
              <VehicleServiceContractInformation
                visible={visible}
                vsc={vsc}
                vendors={productsOptions?.vsc?.vendors}
                contractVsc={contractVsc}
                tpapVsc={productsMetadata?.tpapVsc}
                access={vscAccess}
                contractValuesUnavailable={productsMetadata?.contractValuesUnavailable}
              />
            )}
            {showGap && (
              <GapInformation
                visible={visible}
                gap={gap}
                vendors={productsOptions?.gap?.vendors}
                contractGap={contractGap}
                tpapGap={productsMetadata?.tpapGap}
                access={gapAccess}
                contractValuesUnavailable={productsMetadata?.contractValuesUnavailable}
              />
            )}
          </div>
        )}
        {vendorOptionsUnavailable && (
          <p role="alert" className="text-2xs text-warning px-3">
            {t("vehicle.vendorOptionsUnavailable")}
          </p>
        )}
        {productsMetadata?.flagsUnreliable && (
          <p role="alert" className="text-2xs text-warning px-3">
            {t("vehicle.productFlagsUnreliable")}
          </p>
        )}
      </div>
    </Section>
  );
}

export default Vehicle;
